import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const TABS = [
    { key: "all", label: "Tutte" },
    { key: "alert", label: "Alert" },
    { key: "assignment", label: "Assegnazioni" },
    { key: "report_ready", label: "Report" },
];

function severityColor(kind, sev) {
    if (kind === "alert") {
        const s = (sev || "").toLowerCase();
        if (s.includes("critico") || s.includes("alto")) return "text-[#DC2626]";
        if (s.includes("attenzione") || s.includes("medio")) return "text-[#F59E0B]";
    }
    if (kind === "assignment") return "text-[#2563EB]";
    if (kind === "report_ready") return "text-[#16A34A]";
    return "text-[#64748B]";
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Rome",
        });
    } catch { return iso; }
}

export function NotificationsBell() {
    const [count, setCount] = useState(0);
    const [alertsCount, setAlertsCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();

    const fetchCount = useCallback(async () => {
        try {
            const [{ data: n }, { data: a }] = await Promise.all([
                api.get("/notifications/unread-count"),
                api.get("/alerts/unread-count").catch(() => ({ data: { count: 0 } })),
            ]);
            setCount(Number(n?.count || 0));
            setAlertsCount(Number(a?.count || 0));
        } catch { /* silent */ }
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 20, is_read: false };
            if (tab !== "all") params.kind = tab;
            const { data } = await api.get("/notifications", { params });
            setItems(data?.items || []);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, [tab]);

    useEffect(() => {
        fetchCount();
        const id = setInterval(fetchCount, 30000);
        return () => clearInterval(id);
    }, [fetchCount]);

    useEffect(() => {
        if (open) fetchList();
    }, [open, tab, fetchList]);

    useEffect(() => {
        function onClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    const markRead = async (n) => {
        try {
            await api.post(`/notifications/${n.id}/read`);
            if (n.alert_id) {
                try { await api.post(`/alerts/${n.alert_id}/acknowledge`); } catch { /* may already be ack */ }
            }
            setItems((prev) => prev.filter((x) => x.id !== n.id));
            fetchCount();
        } catch { /* ignore */ }
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems([]);
            fetchCount();
        } catch { /* ignore */ }
    };

    const openItem = (n) => {
        markRead(n);
        if (n.url) {
            setOpen(false);
            // Only navigate SPA routes, not API URLs
            if (n.url.startsWith("/") && !n.url.startsWith("/api/")) navigate(n.url);
        }
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                data-testid="alerts-bell"
                onClick={() => setOpen((v) => !v)}
                className="relative p-2 rounded-lg text-[#64748B] hover:text-[#172033] hover:bg-[#F5F7FB] transition-colors"
                aria-label="Notifiche"
            >
                <Bell className="w-5 h-5" strokeWidth={1.75} />
                {(count > 0 || alertsCount > 0) && (
                    <span
                        data-testid="alerts-badge-count"
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center leading-none"
                    >
                        {Math.max(count, alertsCount) > 99 ? "99+" : Math.max(count, alertsCount)}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="alerts-dropdown"
                    className="absolute right-0 mt-2 w-[420px] bg-white border border-[#DCE3EE] rounded-xl shadow-lg z-50 overflow-hidden"
                >
                    <div className="px-4 py-3 border-b border-[#DCE3EE] flex items-center justify-between">
                        <div className="text-sm font-semibold text-[#172033]">Centro notifiche</div>
                        <button
                            data-testid="notifications-mark-all-read"
                            onClick={markAllRead}
                            className="text-xs text-[#2563EB] hover:underline"
                        >
                            Segna tutte come lette
                        </button>
                    </div>
                    <div className="flex border-b border-[#DCE3EE] text-xs">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                data-testid={`notif-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`flex-1 px-3 py-2 font-medium ${tab === t.key ? "text-[#0B1633] border-b-2 border-[#2563EB]" : "text-[#64748B] hover:text-[#172033]"}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="px-4 py-6 text-center text-sm text-[#64748B]">Caricamento…</div>
                        ) : items.length === 0 ? (
                            <div data-testid="notifications-empty" className="px-4 py-6 text-center text-sm text-[#64748B]">
                                Nessuna notifica non letta
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#DCE3EE]">
                                {items.map((n) => (
                                    <li
                                        key={n.id}
                                        data-testid={`notification-item-${n.id}`}
                                        className="px-4 py-3 hover:bg-[#F5F7FB] transition-colors cursor-pointer"
                                        onClick={() => openItem(n)}
                                    >
                                        <div className={`text-xs font-semibold uppercase ${severityColor(n.kind, n.title)}`}>
                                            {n.kind}
                                        </div>
                                        <div className="text-sm font-medium text-[#172033]">{n.title}</div>
                                        {n.body && <div className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{n.body}</div>}
                                        <div className="text-[11px] text-[#94A3B8] mt-1">{formatDate(n.created_at)}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="px-4 py-2 border-t border-[#DCE3EE] text-center">
                        <button
                            data-testid="notifications-view-all"
                            onClick={() => { setOpen(false); navigate("/notifications"); }}
                            className="text-xs text-[#2563EB] hover:underline"
                        >
                            Vedi tutte →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Backward compatibility
export const AlertBell = NotificationsBell;
export default NotificationsBell;
